import { AlertTriangle, RefreshCw } from "lucide-react";

import Button from "@/components/buttons/Button";
import { isReturnedForCorrection, RETURNED_FOR_CORRECTION_LABEL } from "@/utils/application-status";
import type { UnifiedApproval } from "../../services/applications-services";

/**
 * The banner an applicant sees when a reviewer sent their application back to
 * be corrected. Deliberately not styled as a rejection: the application is
 * still alive, and the customer is the one who has to act.
 *
 * Detection uses `UnifiedApproval.currentStatus`, which is the only field that
 * carries the raw RETURNED_FOR_CORRECTION enum name. The list and detail
 * payloads run their status through UnifiedApprovalDTO.mapToSimpleStatus, which
 * has no case for it and so reports the "Processing" default instead.
 *
 * `returnedFromPhase` is recorded on the entity but is not mapped onto any
 * response DTO, so the phase is derived from whichever phase the approval
 * payload reports as rejected / needing clarification. When nothing in the
 * payload identifies it, no phase is named rather than a guess being shown.
 */

const RETURNING_PHASE_PATTERN = /reject|declin|clarif/i;

function hasText(value: string | null | undefined): value is string {
    return typeof value === "string" && value.trim() !== "";
}

type PhaseCandidate = {
    title: string;
    status?: string | null;
    simpleStatus?: string | null;
    comment?: string | null;
};

/** The last phase the approval payload reports as having sent the application back. */
function findReturningPhase(approval: UnifiedApproval): PhaseCandidate | undefined {
    const candidates: PhaseCandidate[] = [];

    const legal = approval.legalPhase;
    if (legal) {
        candidates.push({
            title: "Legal review",
            status: legal.status,
            simpleStatus: legal.simpleStatus,
            comment: legal.comment,
        });
    }

    const documents = approval.documentPhase;
    if (documents) {
        const rejectedFinding = (documents.documentFindings ?? []).find(
            (finding) => finding && finding.approved === false && hasText(finding.comments)
        );
        candidates.push({
            title: "Document review",
            status: documents.status,
            simpleStatus: documents.simpleStatus,
            comment: rejectedFinding?.comments,
        });
    }

    const inspection = approval.inspectionPhase;
    if (inspection) {
        candidates.push({
            title: "Inspection",
            status: inspection.status,
            simpleStatus: inspection.simpleStatus,
            comment: inspection.comment ?? inspection.inspection?.feedback,
        });

        const inspectionDecision = inspection.finalDecision;
        if (inspectionDecision) {
            candidates.push({
                title: "Inspection decision",
                status: inspectionDecision.status,
                simpleStatus: inspectionDecision.simpleStatus,
                comment: inspectionDecision.comment,
            });
        }
    }

    const final = approval.finalApprovalPhase;
    if (final) {
        candidates.push({
            title: "Final decision",
            status: final.status,
            simpleStatus: final.simpleStatus,
            comment: final.comment,
        });
    }

    const returning = candidates.filter(
        (candidate) =>
            (hasText(candidate.status) && RETURNING_PHASE_PATTERN.test(candidate.status)) ||
            (hasText(candidate.simpleStatus) && RETURNING_PHASE_PATTERN.test(candidate.simpleStatus))
    );

    return returning[returning.length - 1];
}

/** The `{ message }` body the API sends with its 409, when there is one. */
function backendMessage(error: unknown): string | undefined {
    const message = (error as { response?: { data?: { message?: string } } } | null | undefined)
        ?.response?.data?.message;
    return hasText(message) ? message : undefined;
}

export type ReturnedForCorrectionNoticeProps = {
    approval?: UnifiedApproval | null;
    /**
     * The reviewer's reason as carried on the application record itself
     * (`statusComment`), for the endpoints that expose it. Used only when the
     * approval payload has no comment on the returning phase.
     */
    fallbackReason?: string | null;
    onResubmit: () => void;
    isResubmitting?: boolean;
    /** The resubmit mutation's error, so the API's own message can be shown. */
    error?: unknown;
};

export default function ReturnedForCorrectionNotice({
    approval,
    fallbackReason,
    onResubmit,
    isResubmitting,
    error,
}: ReturnedForCorrectionNoticeProps) {
    if (!approval || !isReturnedForCorrection(approval.currentStatus)) return null;

    const phase = findReturningPhase(approval);
    const reason = hasText(phase?.comment) ? phase?.comment : fallbackReason;
    const failure = backendMessage(error);

    return (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 size-5 shrink-0 text-amber-600" />
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                            {RETURNED_FOR_CORRECTION_LABEL}
                        </p>
                        <h2 className="text-base font-bold text-[#2C2C2C]">
                            This application needs your attention
                        </h2>
                        <p className="mt-1 text-sm text-amber-900">
                            {phase
                                ? `It was sent back at the ${phase.title.toLowerCase()} stage. Correct what is described below and resubmit — it then goes straight back to that stage.`
                                : "It was sent back for you to correct. Once you resubmit, it goes back to the stage that returned it."}
                        </p>
                    </div>
                </div>

                <Button
                    intent="primary"
                    size="md"
                    loading={isResubmitting}
                    disabled={isResubmitting}
                    leftIcon={<RefreshCw className="size-4" />}
                    onClick={onResubmit}
                >
                    {isResubmitting ? "Resubmitting…" : "Resubmit"}
                </Button>
            </div>

            {hasText(reason) && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-white p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                        What the reviewer asked you to fix
                    </p>
                    <p className="mt-1 whitespace-pre-line text-sm text-gray-800">{reason}</p>
                </div>
            )}

            {failure && (
                <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    {failure}
                </p>
            )}
        </div>
    );
}
